import * as React from 'react';
import cn from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useAtomValue } from 'jotai/utils';

import { CaretDown, CaretLeft, CaretRight, CaretUp } from 'src/common/icons';
import useRoleManager from 'src/hooks/useRoleManager';
import { useSubscriptionQuery } from 'src/api/queries/subscriptionQueries';
import { useAllNotificationsQuery } from 'src/api/queries/notificationQueries';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { featureAvailabilityAtom } from 'src/shared/features/availabilityAtoms';
import { config } from 'src/utils/config';
import { lockScroll, unlockScroll } from 'src/utils/bodyScroll';

import {
  navItems,
  NavItem,
  NavGateCtx,
  NavSection,
  SECTION_ORDER,
  SECTION_TITLES,
} from './navItems';
import useActiveNavId from './useActiveNavId';
import { KEEP_OVERLAY_ATTR, SHELL_SIDEBAR_ID } from 'src/hooks/useShellOverlay';
import useSidebarCollapse, { foldedSectionsAtom } from './useSidebarCollapse';
import AccountMenu from './AccountMenu';
import messages from './messages';
import styles from './AppSidebar.module.scss';

interface ItemRowProps {
  item: NavItem;
  collapsed: boolean;
  active: boolean;
  /** `true` renders an unread dot; a number renders a count.
   *
   *  A count needs both forms because the rail collapses to 20px of icon:
   *  expanded it is a pill after the label, collapsed it degrades to the
   *  same dot a boolean gets, since a number would be clipped. `0` renders
   *  nothing — see the explicit `> 0` test in the component (a bare
   *  `{count && ...}` would print a literal "0"). */
  badge?: boolean | number;
  /** Feature's backend API isn't available yet — render visible but
   *  non-navigating with an "enabling soon" hint. */
  disabled?: boolean;
  onSelect: () => void;
}

const SOON_TOOLTIP = 'Not available yet — enabling soon';

const ItemRow: React.FC<ItemRowProps> = ({ item, collapsed, active, badge, disabled, onSelect }) => {
  const { formatMessage } = useIntl();
  const Icon = item.icon;
  const labelText = formatMessage(item.label);

  const count = typeof badge === 'number' ? badge : 0;
  const showCount = count > 0 && !collapsed;
  // A dot stands in for the count whenever the number cannot be shown: for a
  // boolean badge, and for a count on the collapsed rail.
  const showDot = badge === true || (count > 0 && collapsed);

  // When collapsed, use the label as a native tooltip on the row — with the
  // count spelled out, since the dot alone does not carry it.
  const tooltip = collapsed
    ? count > 0
      ? `${labelText} (${count})`
      : labelText
    : undefined;

  if (disabled) {
    return (
      <li className={cn(styles.item, styles.itemDisabled)}>
        <span
          className={styles.link}
          aria-disabled
          title={collapsed ? `${labelText} — ${SOON_TOOLTIP}` : SOON_TOOLTIP}
          data-test={item.dataTest}
        >
          <span className={styles.icon} aria-hidden>
            <Icon />
          </span>
          <span className={styles.label}>{labelText}</span>
          {!collapsed && <span className={styles.soonBadge}>Soon</span>}
        </span>
      </li>
    );
  }

  return (
    <li
      className={cn(styles.item, { [styles.itemActive]: active })}
      aria-current={active ? 'page' : undefined}
    >
      <NavLink
        to={item.to}
        className={styles.link}
        onClick={onSelect}
        title={tooltip}
        data-test={item.dataTest}
      >
        <span className={styles.icon} aria-hidden>
          <Icon />
          {showDot && <span className={styles.badgeDot} />}
        </span>
        <span className={styles.label}>{labelText}</span>
        {showCount && (
          <span className={styles.badgeCount} aria-label={`${count} waiting`}>
            {count}
          </span>
        )}
      </NavLink>
    </li>
  );
};

export const AppSidebar: React.FC = () => {
  const { formatMessage } = useIntl();
  const { isAdmin, isManager, isUser } = useRoleManager();
  const addonsSettings = useAtomValue(addonsSettingsAtom);
  const featureAvailability = useAtomValue(featureAvailabilityAtom);
  const aiAddonEnabled = Boolean(
    addonsSettings?.AI_ASSISTANT?.added && addonsSettings?.AI_ASSISTANT?.enabled,
  );
  // Admin-only surface, so we only fire the query for admins. (The backend
  // now returns 403 — not 401 — for authenticated non-admins, so this is no
  // longer a logout hazard; the gate is just scoping.)
  const { data: subscription } = useSubscriptionQuery({
    enabled: config.showSubscription && isAdmin,
  });

  // Poll notifications so the Activity sidebar item can show an unread dot.
  // The dismiss mutation (on the /notifications page) optimistically updates
  // the same ['notification','all'] cache, so this clears in lock-step.
  const { data: notificationsData } = useAllNotificationsQuery(undefined, {
    refetchIntervalInBackground: true,
    refetchInterval: config.notificationsRefetchInterval,
  });
  const hasNotifications = (notificationsData?.notifications?.length ?? 0) > 0;

  // Reviews assigned TO this user and not yet resolved — the count on
  // "Assigned to me". Derived from the poll above rather than a second request:
  // the tasks are already in that payload, and `useResolveTaskMutation`
  // prunes the resolved row from the same ['notification','all'] cache, so
  // this clears the moment the user acts on the page.
  const openTaskCount = React.useMemo(
    () =>
      (notificationsData?.notifications ?? []).filter(
        (n: { category?: string; status?: string }) =>
          n.category === 'tasks' && n.status === 'OPEN',
      ).length,
    [notificationsData],
  );

  const {
    collapsed,
    toggleCollapsed,
    isMobile,
    mobileOpen,
    closeMobile,
  } = useSidebarCollapse();
  const activeId = useActiveNavId();
  const [foldedSections, setFoldedSections] = useAtom(foldedSectionsAtom);

  const toggleSection = React.useCallback(
    (section: NavSection) =>
      setFoldedSections((cur) => ({ ...cur, [section]: !cur[section] })),
    [setFoldedSections],
  );

  const gateCtx: NavGateCtx = React.useMemo(
    () => ({
      isAdmin,
      isManager,
      isUser,
      subscriptionVisible: Boolean(subscription),
      aiAddonEnabled,
      featureAvailability,
      config: {
        showSubscription: config.showSubscription,
        showQueueStats: config.showQueueStats,
        showTaskQueues: config.showTaskQueues,
        showDeliveryAnalytics: config.showDeliveryAnalytics,
        showMigration: config.showMigration,
      },
    }),
    [isAdmin, isManager, isUser, subscription, aiAddonEnabled, featureAvailability],
  );

  const visibleItems = React.useMemo(
    () => navItems.filter((it) => it.gate(gateCtx)),
    [gateCtx],
  );

  // Group by section (preserving declaration order)
  const grouped = React.useMemo(() => {
    const bySection: Record<NavSection, NavItem[]> = {
      main: [],
      workflows: [],
      insights: [],
      account: [],
      advancedTools: [],
      configure: [],
    };
    for (const item of visibleItems) bySection[item.section].push(item);
    return bySection;
  }, [visibleItems]);

  const handleSelect = React.useCallback(() => {
    // Tapping a link inside the mobile drawer should close it (matches
    // the support app behaviour from static/js/main/app-shell.js).
    if (isMobile && mobileOpen) closeMobile();
  }, [isMobile, mobileOpen, closeMobile]);

  // Pin background scroll while the mobile drawer is open so the page
  // underneath doesn't rubber-band on iOS Safari.
  React.useEffect(() => {
    if (!(isMobile && mobileOpen)) return;
    lockScroll();
    return () => unlockScroll();
  }, [isMobile, mobileOpen]);

  return (
    <>
      <aside
        className={cn(styles.sidebar, {
          [styles.sidebarCollapsed]: collapsed,
          [styles.sidebarMobileOpen]: isMobile && mobileOpen,
        })}
        aria-label={formatMessage(messages.primaryNavigation)}
        id={SHELL_SIDEBAR_ID}
        data-test="app-sidebar"
      >
        {!isMobile && (
          <button
            type="button"
            className={styles.toggle}
            onClick={toggleCollapsed}
            aria-label={formatMessage(
              collapsed ? messages.toggleExpand : messages.toggleCollapse,
            )}
            aria-expanded={!collapsed}
            aria-controls="app-sidebar-nav"
            data-test="sidebar-toggle"
            /* A width preference, not a navigation: the rail is visible beside
               a content-area overlay, so this must not dismiss an open asset. */
            {...{ [KEEP_OVERLAY_ATTR]: true }}
          >
            {collapsed ? <CaretRight /> : <CaretLeft />}
          </button>
        )}

        <nav className={styles.nav} id="app-sidebar-nav">
          {SECTION_ORDER.map((section) => {
            const items = grouped[section];
            if (!items || items.length === 0) return null;

            // Sections without a SECTION_TITLES entry render headerless
            // (`main`) — no heading, no fold toggle, items always visible.
            // Only titled sections can fold.
            const title = SECTION_TITLES[section];
            // Folding only applies to the expanded desktop sidebar. In the
            // collapsed rail (icon-only) the section titles are hidden, so
            // we always show the items there.
            const folded = !collapsed && Boolean(title) && Boolean(foldedSections[section]);
            const sectionName = title ? formatMessage(title) : null;

            return (
              <div className={styles.section} key={section} data-section={section}>
                {sectionName && (
                  <button
                    type="button"
                    className={styles.sectionTitle}
                    onClick={() => toggleSection(section)}
                    aria-expanded={!folded}
                    aria-label={formatMessage(
                      folded ? messages.sectionUnfold : messages.sectionFold,
                      { section: sectionName },
                    )}
                  >
                    <span className={styles.sectionTitleText}>{sectionName}</span>
                    {/* Stripe-style affordance: UP when open (click to
                        close), DOWN when folded (click to open). */}
                    <span className={styles.sectionCaret} aria-hidden>
                      {folded ? <CaretDown /> : <CaretUp />}
                    </span>
                  </button>
                )}
                {!folded && (
                  <ul className={styles.list}>
                    {items.map((item) => (
                      <ItemRow
                        key={item.id}
                        item={item}
                        collapsed={collapsed}
                        active={item.id === activeId}
                        badge={
                          item.id === 'my-approvals'
                            ? openTaskCount
                            : item.id === 'notifications' && hasNotifications
                        }
                        disabled={
                          !!item.featureId &&
                          featureAvailability[item.featureId] === 'unavailable'
                        }
                        onSelect={handleSelect}
                      />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        <AccountMenu
          orgItems={grouped.account}
          subscription={subscription}
          collapsed={collapsed}
          visible={!isMobile || mobileOpen}
          onNavigate={handleSelect}
        />
      </aside>

      {isMobile && (
        <div
          className={cn(styles.mobileScrim, {
            [styles.mobileScrimVisible]: mobileOpen,
          })}
          onClick={closeMobile}
          aria-hidden
        />
      )}
    </>
  );
};

export default AppSidebar;
